import React, { useCallback, useContext } from 'react';
import ViewContext from '../Context/ViewContext';
import WorkspaceContext from '../Context/WorkspaceContext';
import { useDispatch } from 'react-redux';
import { getNumericFields } from '../../utils/FieldUtil';
import styles from '../../styles/partials/_viewtoolbar.css';
import ColumnSelector from '../view/ColumnSelector';
import { Analytics } from '../../mixpanel';
import { viewActions } from '../../state/modules/view';

/**
 * Workspace column selector component for use in a Toolbar - Allows selection of columns/column totals.
 */
const GridToolbarWorkspaceColumnSelector = ({
  tabEnable = true,
  onResetClick,
}: {
  tabEnable?: boolean;
  onResetClick?: () => void;
}) => {
  const viewContext = useContext(ViewContext);
  const { availableColumns, viewScreenName } = viewContext;
  const { entity, availableColumnsFilter } = useContext(WorkspaceContext);
  const dispatch = useDispatch();

  const numericFields = entity ? getNumericFields(entity, entity.workspace) : null;

  const updateColumns = useCallback(
    (columns: Array<any>) => {
      const selectedColumns = columns.filter((column) => {
        return column.selected;
      });
      Analytics.track('Workspace columns updated', { screenName: viewScreenName });
      dispatch(viewActions.updateColumns(viewScreenName, selectedColumns));
    },
    [dispatch, viewScreenName]
  );

  // Only render column selector once view details exist (even Workspace default view will do)
  // Only render if in grid view
  if (!viewContext.currentView) {
    return null;
  }

  const filteredAvailableColumns =
    availableColumns && availableColumnsFilter ? availableColumns.filter(availableColumnsFilter) : availableColumns;

  const selectedFieldNames: Array<string> = [];
  const totalFieldNames: Array<string> = [];
  viewContext.currentView?.columns.forEach((column) => {
    if (!column.fieldName) {
      return;
    }
    if (column.title) {
      selectedFieldNames.push(column.fieldName);
    }
    if (column.total) {
      totalFieldNames.push(column.fieldName);
    }
  });

  return (
    <div className={styles.columnSelectorContainer}>
      <div className={styles.columnSelector}>
        <ColumnSelector
          tabIndex={tabEnable ? 1 : -1}
          extraClass="ws-column-select"
          availableColumns={filteredAvailableColumns ?? []}
          selectedFieldNames={selectedFieldNames}
          updateColumns={updateColumns}
          alignLeft={true}
          showClearAll={false}
          data-testid="grid_column"
          numericFields={numericFields}
          totalFieldNames={totalFieldNames}
          onResetClick={onResetClick}
        />
      </div>
    </div>
  );
};

export default GridToolbarWorkspaceColumnSelector;
